"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { default as NextImage } from "next/image";

import TopBar from "./TopBar";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@radix-ui/react-navigation-menu";

import {
  ChevronDown,
  Menu,
  X,
  Shirt,
  Building2,
  ShoppingCart,
  Users,
  Store,
  Recycle,
  Scissors,
  Sparkles,
  Code2,
  Smartphone,
  LayoutTemplate,
  ShoppingBag,
  Briefcase,
  Handshake,
  LifeBuoy,
  Phone,
  Mail,
  Star,
  BookOpen,
} from "lucide-react";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const mobileWrapRef = useRef<HTMLDivElement | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [menuTop, setMenuTop] = useState(0);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const navWrapRef = useRef<HTMLDivElement | null>(null);

  const blockHoverOpen = {
    onPointerMove: (e: React.PointerEvent) => e.preventDefault(),
    onPointerLeave: (e: React.PointerEvent) => e.preventDefault(),
  };

  const mobileSections = [
    {
      title: "Großhandel",
      items: [
        { href: "/modeprofi", label: "ModeProfi", icon: Shirt },
        { href: "/office4trade", label: "Office4Trade", icon: Building2 },
        { href: "/b2b", label: "B2B-Onlineshop", icon: ShoppingCart },
        { href: "/crm4trade", label: "CRM4Trade", icon: Users },
      ],
    },
    {
      title: "Einzelhandelbranchen",
      items: [
        {
          href: "/kassensysteme/textil",
          label: "Textileinzelhandel",
          icon: Shirt,
        },
        {
          href: "/kassensysteme/schuhe",
          label: "Schuheinzelhandel",
          icon: Store,
        },
        {
          href: "/kassensysteme/secondhand",
          label: "Second-Hand",
          icon: Recycle,
        },
        {
          href: "/kassensysteme/getraenke",
          label: "Getränkemarkt",
          icon: ShoppingCart,
        },
        {
          href: "/kassensysteme/friseur-kosmetik",
          label: "Friseur & Kosmetik",
          icon: Scissors,
        },
        {
          href: "/kassensysteme/brautmode",
          label: "Brautmode",
          icon: Sparkles,
        },
      ],
    },
    {
      title: "App-Entwicklung",
      items: [
        {
          href: "/customized-apps",
          label: "Customized-Apps",
          icon: Smartphone,
        },
        {
          href: "/softwareentwicklung",
          label: "Softwareentwicklung",
          icon: Code2,
        },
      ],
    },
    {
      title: "Web-Entwicklung",
      items: [
        { href: "/webdesign", label: "Webdesign", icon: LayoutTemplate },
        {
          href: "/shopentwicklung",
          label: "Shopentwicklung",
          icon: ShoppingBag,
        },
      ],
    },
    {
      title: "Über Uns",
      items: [
        { href: "/ueber-uns", label: "Firmenprofil", icon: Building2 },
        { href: "/karriere", label: "Karriere", icon: Briefcase },
        { href: "/partner", label: "Partner", icon: Handshake },
        { href: "/service", label: "Service", icon: LifeBuoy },
        { href: "/referenzen", label: "Referenzen", icon: Star },
        { href: "https://support.sdigroup.de/", label: "Wiki", icon: BookOpen },
      ],
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!mobileOpen) return;
      if (!mobileWrapRef.current) return;
      if (
        event.target instanceof Node &&
        !mobileWrapRef.current.contains(event.target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutsideNav = (event: MouseEvent) => {
      if (!navWrapRef.current) return;
      if (
        event.target instanceof Node &&
        !navWrapRef.current.contains(event.target)
      ) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("click", handleClickOutsideNav);
    return () => document.removeEventListener("click", handleClickOutsideNav);
  }, []);

  const updateMenuTop = () => {
    if (!stickyHeaderRef.current) return;
    const rect = stickyHeaderRef.current.getBoundingClientRect();
    setMenuTop(rect.bottom);
  };

  useEffect(() => {
    updateMenuTop();
    window.addEventListener("scroll", updateMenuTop);
    window.addEventListener("resize", updateMenuTop);
    return () => {
      window.removeEventListener("scroll", updateMenuTop);
      window.removeEventListener("resize", updateMenuTop);
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      updateMenuTop();
      requestAnimationFrame(updateMenuTop);
    });
  }, [openMenu]);

  useEffect(() => {
    const handleScrollCloseMenus = () => {
      if (openMenu) setOpenMenu(null);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    if (!mobileOpen) {
      window.addEventListener("scroll", handleScrollCloseMenus);
    }
    return () => window.removeEventListener("scroll", handleScrollCloseMenus);
  }, [mobileOpen, openMenu]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const toggleMenu = (key: string) => {
    setOpenMenu((prev) => (prev === key ? null : key));
  };

  const dropdownClass =
    "fixed left-0 right-0 bg-slate-50 shadow-xl z-[999] py-8";
  const dropdownInner = "mx-auto max-w-[1100px] px-6";
  const grid4 =
    "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-3";
  const grid3 =
    "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-3";
  const grid2 =
    "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-x-10 gap-y-3";

  return (
    <>
      <TopBar />

      <div
        ref={stickyHeaderRef}
        className="sticky top-0 w-full bg-white border-gray-200 border-b shadow-[0_4px_20px_rgba(0,0,0,0.06)] z-110 overflow-visible"
      >
        <div className="flex items-center justify-center pl-14 pr-10 py-1 md:py-3 max-w-7xl mx-auto w-full">
          {/* Logo – shrink-0 damit es nie gequetscht wird */}
          <Link
            href="/"
            onClick={(e) => {
              e.preventDefault();
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
              if (window.location.pathname !== "/") {
                window.location.href = "/";
              }
            }}
            className="flex items-center space-x-2 shrink-0"
          >
            <NextImage
              src="/logo.svg"
              alt="Logo"
              width={90}
              height={60}
              className="h-12 md:h-17.5 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div ref={navWrapRef}>
            <NavigationMenu
              className="hidden lg:flex ms-5"
              value={openMenu ?? undefined}
              onValueChange={(val) => setOpenMenu(val || null)}
            >
              <NavigationMenuList className="flex flex-row gap-0 lg:gap-2 items-center">
                {/* Großhandel */}
                <NavigationMenuItem
                  value="grosshandel"
                  className="px-2 lg:px-4 py-2"
                >
                  <NavigationMenuTrigger
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    {...blockHoverOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("grosshandel");
                    }}
                  >
                    Großhandel <ChevronDown size={16} className="inline ml-1" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    {...blockHoverOpen}
                    style={{ top: menuTop }}
                    className={dropdownClass}
                  >
                    <div className={dropdownInner}>
                      <ul className={grid4}>
                        <li>
                          <NavigationMenuLink
                            href="/modeprofi"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Shirt size={20} className="nav-icon" />
                                <div className="nav-card-title">ModeProfi</div>
                              </div>
                              <div className="nav-card-desc">
                                Warenwirtschaftssystem für Mode- und
                                Einzelhandel.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/office4trade"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Building2 size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Office4Trade
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Flexible Software für Großhandel & B2B-Prozesse.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink href="/b2b" className="nav-card">
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <ShoppingCart size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  B2B-Onlineshop
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Shopware-Anbindung für deinen Großhandel.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/crm4trade"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Users size={20} className="nav-icon" />
                                <div className="nav-card-title">CRM4Trade</div>
                              </div>
                              <div className="nav-card-desc">
                                Kunden- & Kontaktmanagement im Großhandel.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Einzelhandelbranchen */}
                <NavigationMenuItem
                  value="einzelhandel"
                  className="px-2 lg:px-4 py-2"
                >
                  <NavigationMenuTrigger
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    {...blockHoverOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("einzelhandel");
                    }}
                  >
                    Einzelhandel{" "}
                    <ChevronDown size={16} className="inline ml-1" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    {...blockHoverOpen}
                    style={{ top: menuTop }}
                    className={dropdownClass}
                  >
                    <div className={dropdownInner}>
                      <ul className={grid3}>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/textil"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Shirt size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Textileinzelhandel
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Kasse & Warenwirtschaft für Mode- & Textilläden.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/schuhe"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Store size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Schuheinzelhandel
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Optimierte Abläufe für Schuhfachgeschäfte.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/secondhand"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Recycle size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Second-Hand
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Funktionen für Ankauf, Verkauf & Kommission.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/getraenke"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <ShoppingCart size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Getränkemarkt
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Kasse mit Pfand, Kisten & Mehrweg-Verwaltung.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/friseur-kosmetik"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Scissors size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Friseur- & Kosmetiksalon
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Termine, Dienstleistungen & Produktverkauf im
                                Blick.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/kassensysteme/brautmode"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Sparkles size={20} className="nav-icon" />
                                <div className="nav-card-title">Brautmode</div>
                              </div>
                              <div className="nav-card-desc">
                                Reservierungen & Aufträge verwalten.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* App-Entwicklung */}
                <NavigationMenuItem
                  value="app-entwicklung"
                  className="px-2 lg:px-4 py-2"
                >
                  <NavigationMenuTrigger
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    {...blockHoverOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("app-entwicklung");
                    }}
                  >
                    App-Entwicklung{" "}
                    <ChevronDown size={16} className="inline ml-1" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    {...blockHoverOpen}
                    style={{ top: menuTop }}
                    className={dropdownClass}
                  >
                    <div className={dropdownInner}>
                      <ul className={grid2}>
                        <li>
                          <NavigationMenuLink
                            href="/customized-apps"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Smartphone size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Customized-Apps
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Individuelle Apps für iOS & Android – passend zu
                                deinen Prozessen.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/softwareentwicklung"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Code2 size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Softwareentwicklung
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Individuelle Business-Software & Schnittstellen
                                für dein Unternehmen.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Web-Entwicklung */}
                <NavigationMenuItem
                  value="web-entwicklung"
                  className="px-2 lg:px-4 py-2"
                >
                  <NavigationMenuTrigger
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    {...blockHoverOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("web-entwicklung");
                    }}
                  >
                    Web-Entwicklung{" "}
                    <ChevronDown size={16} className="inline ml-1" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    {...blockHoverOpen}
                    style={{ top: menuTop }}
                    className={dropdownClass}
                  >
                    <div className={dropdownInner}>
                      <ul className={grid2}>
                        <li>
                          <NavigationMenuLink
                            href="/webdesign"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <LayoutTemplate
                                  size={20}
                                  className="nav-icon"
                                />
                                <div className="nav-card-title">Webdesign</div>
                              </div>
                              <div className="nav-card-desc">
                                Moderne Websites mit Fokus auf Design,
                                Performance & Conversion.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/shopentwicklung"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <ShoppingBag size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Shopentwicklung
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                E-Commerce Lösungen mit Shopware – individuell &
                                skalierbar.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Preise */}
                <NavigationMenuItem className="px-2 lg:px-4 py-2">
                  <NavigationMenuLink
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    href="/preise"
                  >
                    Preise
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Kontakt */}
                <NavigationMenuItem className="px-2 lg:px-4 py-2">
                  <NavigationMenuLink
                    className="hover:text-(--nav-hover) text-sm lg:text-base"
                    href="/kontakt"
                  >
                    Kontakt
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Über uns */}
                <NavigationMenuItem
                  value="ueber-uns"
                  className="px-2 lg:px-4 py-2"
                >
                  <NavigationMenuTrigger
                    className="hover:text-(--nav-hover) text-sm lg:text-base whitespace-nowrap"
                    {...blockHoverOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("ueber-uns");
                    }}
                  >
                    Über uns <ChevronDown size={16} className="inline ml-1" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    {...blockHoverOpen}
                    style={{ top: menuTop }}
                    className={dropdownClass}
                  >
                    <div className={dropdownInner}>
                      <ul className={grid3}>
                        <li>
                          <NavigationMenuLink
                            href="/ueber-uns"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Building2 size={20} className="nav-icon" />
                                <div className="nav-card-title">
                                  Firmenprofil
                                </div>
                              </div>
                              <div className="nav-card-desc">
                                Wer wir sind, wofür wir stehen und wie wir
                                arbeiten.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/karriere"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Briefcase size={20} className="nav-icon" />
                                <div className="nav-card-title">Karriere</div>
                              </div>
                              <div className="nav-card-desc">
                                Werde Teil unseres Teams und entwickle Lösungen
                                mit uns.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/partner"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Handshake size={20} className="nav-icon" />
                                <div className="nav-card-title">Partner</div>
                              </div>
                              <div className="nav-card-desc">
                                Gemeinsam mit starken Partnern zum besten
                                Ergebnis.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/service"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <LifeBuoy size={20} className="nav-icon" />
                                <div className="nav-card-title">Service</div>
                              </div>
                              <div className="nav-card-desc">
                                Support, Schulung & Betreuung - persönlich &
                                digital.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink
                            href="/referenzen"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <Star size={20} className="nav-icon" />
                                <div className="nav-card-title">Referenzen</div>
                              </div>
                              <div className="nav-card-desc">
                                Erfolgreiche Projekte & zufriedene Kunden im
                                Überblick.
                              </div>
                            </div>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <a
                            href="https://support.sdigroup.de/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="nav-card"
                          >
                            <div className="nav-card-inner">
                              <div className="flex items-center gap-2">
                                <BookOpen size={20} className="nav-icon" />
                                <div className="nav-card-title">Wiki</div>
                              </div>
                              <div className="nav-card-desc">
                                Internes Wissen, Anleitungen & Dokumentation auf
                                einen Blick.
                              </div>
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Mobile Burger */}
          <div ref={mobileWrapRef} className="lg:hidden ms-auto">
            <button
              className="p-2 rounded-md border border-gray-300"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menü öffnen"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {mobileOpen && (
              <div className="fixed inset-0 z-1000">
                <div
                  className="absolute inset-0 bg-black/35"
                  onClick={() => setMobileOpen(false)}
                />
                <div className="absolute top-0 left-0 right-0 bg-white shadow-2xl border-b border-slate-200 max-h-screen overflow-y-auto">
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-600">
                        Navigation
                      </span>
                      <button
                        className="p-2 rounded-md border border-gray-300"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Menü schließen"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <Link
                        href="/preise"
                        onClick={() => setMobileOpen(false)}
                        className="rounded-xl border border-slate-200 px-4 py-3 text-center font-medium text-slate-900"
                      >
                        Preise
                      </Link>
                      <Link
                        href="/kontakt"
                        onClick={() => setMobileOpen(false)}
                        className="rounded-xl bg-(--brand-700) px-4 py-3 text-center font-medium text-white"
                      >
                        Kontakt
                      </Link>
                    </div>

                    <div className="mt-6 space-y-6 pb-6">
                      {mobileSections.map((section) => (
                        <div key={section.title}>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {section.title}
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-2">
                            {section.items.map((it) => {
                              const Icon = it.icon;
                              const isExternal = it.href.startsWith("http");
                              const className =
                                "flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-slate-900 hover:border-slate-300";

                              return isExternal ? (
                                <a
                                  key={it.href}
                                  href={it.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={className}
                                >
                                  <Icon size={18} className="text-slate-700" />
                                  <span className="font-medium">
                                    {it.label}
                                  </span>
                                </a>
                              ) : (
                                <Link
                                  key={it.href}
                                  href={it.href}
                                  onClick={() => setMobileOpen(false)}
                                  className={className}
                                >
                                  <Icon size={18} className="text-slate-700" />
                                  <span className="font-medium">
                                    {it.label}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      <div className="pt-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Support
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <a
                            href="tel:+49 (0) 6196 - 950 591 0"
                            className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 font-medium text-slate-900"
                          >
                            <Phone size={18} />
                            Anrufen
                          </a>
                          <a
                            href="mailto:info@sdigroup.de"
                            className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 font-medium text-slate-900"
                          >
                            <Mail size={18} />
                            Mail
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
